<template>
  <section class="estimator-result">
    <SoAlert
      v-if="requestFailed"
      type="error"
      title="The estimator isn't currently available"
      class="mb-2"
      data-testid="estimator-unavailable"
    />
    <SoAlert
      v-for="validationError in validationErrors"
      :key="validationError"
      type="error"
      :text="validationError"
      class="mb-2"
      data-testid="estimator-validation-error"
    />
    <SoAlert
      v-for="warning in warnings"
      :key="warning"
      type="warning"
      :text="warning"
      class="mb-2"
      data-testid="estimator-warning"
    />

    <div
      v-for="(row, index) in rows"
      :key="index"
      class="mt-4"
      data-testid="estimator-result-row"
    >
      <SoAlert
        v-if="showsRead && row.wentNegative"
        type="warning"
        text="The projection falls below zero, so this is not a read the meter could show. Check the inputs - a target date before the read it estimates from needs a lower read, not a wrapped one."
        class="mb-2"
        data-testid="estimator-went-negative-warning"
      />
      <SoAlert
        v-else-if="showsRead && row.overflowed"
        type="info"
        text="The register rolls over before this date, so the estimate assumes a rollover."
        class="mb-2"
        data-testid="estimator-overflowed-warning"
      />

      <!-- The answer, signposted: what it is, what it is for, and copyable. -->
      <div class="estimator-answer" data-testid="estimator-answer">
        <p class="estimator-answer__label" data-testid="estimator-answer-label">
          {{ answerLabel(row) }}
        </p>
        <div class="estimator-answer__value-row">
          <!-- A negative projection has no meter reading to show, so show the
               projection itself rather than the believable number it wraps to. -->
          <template v-if="showsRead && row.wentNegative">
            <p
              class="estimator-answer__value"
              data-testid="estimator-estimated-read-unusable"
            >
              {{ row.estimate.estimatedRead.toFixed(1) }}
            </p>
          </template>
          <SoCopyToClipboard
            v-else-if="hasCopyableValue(row)"
            :plain-text="copyValue(row)"
            inline
          >
            <p
              class="estimator-answer__value"
              :data-testid="
                showsRead
                  ? 'estimator-estimated-read'
                  : 'estimator-estimated-annual'
              "
            >
              {{ answerValue(row) }}
            </p>
          </SoCopyToClipboard>
        </div>
        <p v-if="rows.length > 1" class="text-caption mb-0">
          {{ row.registerLabel }}
        </p>
      </div>

      <p
        v-if="showsRead"
        class="text-caption mt-2 mb-0"
        data-testid="estimator-projection-detail"
      >
        {{ projectionDetail(row) }}
      </p>

      <template v-else>
        <p class="mt-2 mb-0" data-testid="estimator-industry-comparison">
          Industry {{ eacOrAq }} {{ round(row.industryEacAq) }} kWh
          <span v-if="row.differenceKwh !== null">
            ({{ signed(row.differenceKwh) }} kWh<template
              v-if="row.differencePercent !== null"
              >, {{ signed(row.differencePercent, 1) }}%</template
            >)
          </span>
        </p>
        <p
          class="text-caption mt-1 mb-0"
          data-testid="estimator-known-period-detail"
        >
          {{ knownPeriodDetail(row) }}
        </p>
      </template>

      <details class="mt-2" data-testid="estimator-breakdown">
        <summary>Breakdown</summary>
        <ul class="text-caption">
          <li>Inputs: {{ row.inputsSummary }}</li>
          <li v-if="row.knownPeriodDays !== null">
            Between the reads: {{ row.knownPeriodDays }} days
          </li>
          <li v-if="row.knownPeriodWeight !== null">
            Weight for that period (ALP): {{ weight(row.knownPeriodWeight) }}
          </li>
          <li v-if="showsRead && row.projectionDays !== null">
            {{ sentenceCase(row.laterReadLabel) }} to target date:
            {{ row.projectionDays }} days
          </li>
          <li v-if="showsRead">
            Weight for that period: {{ weight(row.projectionWeight) }}
          </li>
          <li>Seasonality: {{ seasonal ? "on" : "off" }}</li>
          <li v-if="volumeUnitLabel">Volume unit: {{ volumeUnitLabel }}</li>
          <li v-if="calorificValueApplied !== null">
            Calorific value applied: {{ calorificValueApplied }}
          </li>
          <li>
            Raw projection: {{ rawProjection(row.estimate.estimatedRead) }}
          </li>
        </ul>
      </details>
    </div>
  </section>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoCopyToClipboard from "@/components/SoCopyToClipboard.vue"
  import { IEstimatorResultRow } from "./estimatorResultRows"
  import { formatRawProjection } from "./estimatorSystemRead"

  export default defineComponent({
    components: { SoAlert, SoCopyToClipboard },
    props: {
      rows: {
        type: Array as PropType<IEstimatorResultRow[]>,
        default: () => [],
      },
      validationErrors: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      warnings: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      requestFailed: {
        type: Boolean,
        default: false,
      },
      showsRead: {
        type: Boolean,
        default: true,
      },
      seasonal: {
        type: Boolean,
        default: true,
      },
      eacOrAq: {
        type: String,
        default: "EAC/AC",
      },
      volumeUnitLabel: {
        type: String,
        default: "",
      },
      calorificValueApplied: {
        type: Number as PropType<number | null>,
        default: null,
      },
    },
    methods: {
      rawProjection(value: number): string {
        return formatRawProjection(value)
      },
      round(value: number | null): string {
        if (value === null) return "-"

        return Math.round(value).toLocaleString()
      },
      signed(value: number, precision = 0): string {
        const rounded = value.toFixed(precision)

        return value > 0 ? `+${rounded}` : rounded
      },
      /** The row labels are lower case for mid-sentence use; this line leads. */
      sentenceCase(label: string): string {
        return label.charAt(0).toUpperCase() + label.slice(1)
      },
      weight(value: number | null): string {
        if (value === null) return "-"

        return value.toFixed(3)
      },
      answerLabel(row: IEstimatorResultRow): string {
        if (!this.showsRead) return `Estimated ${this.eacOrAq}`

        return `Estimated read at ${row.targetDateLabel}`
      },
      answerValue(row: IEstimatorResultRow): string {
        if (!this.showsRead) {
          return `${this.round(row.annualConsumptionKwh)} kWh/year`
        }

        return row.read
      },
      /** No copy button for a figure we render as a dash. */
      hasCopyableValue(row: IEstimatorResultRow): boolean {
        return this.showsRead || row.annualConsumptionKwh !== null
      },
      /** The bare figure, so a pasted value needs no tidying up. */
      copyValue(row: IEstimatorResultRow): string {
        if (!this.showsRead) {
          return String(Math.round(row.annualConsumptionKwh ?? 0))
        }

        return row.read
      },
      /**
       * Says which period the projected consumption covers, which "over the
       * period" did not.
       */
      projectionDetail(row: IEstimatorResultRow): string {
        const kwh = this.round(row.projectionConsumptionKwh)
        const days =
          row.projectionDays === null ? "" : ` over ${row.projectionDays} days`

        return `${kwh} kWh expected between the ${row.laterReadLabel} and ${
          row.targetDateLabel
        }${days}, being ${this.weight(
          row.projectionWeight,
        )} of a year's consumption`
      },
      /** The same shape the old EAC/AQ estimator showed, ALP included. */
      knownPeriodDetail(row: IEstimatorResultRow): string {
        const parts: string[] = []

        if (row.knownPeriodConsumptionUnits !== null && row.unitLabel) {
          parts.push(
            `${this.round(row.knownPeriodConsumptionUnits)} ${row.unitLabel}`,
          )
        }
        parts.push(`${this.round(row.knownPeriodConsumptionKwh)} kWh`)

        const measured = parts.join(" / ")
        const days =
          row.knownPeriodDays === null
            ? "the period between the reads"
            : `${row.knownPeriodDays} days between the reads`

        return `${measured} over ${days} (ALP ${this.weight(
          row.knownPeriodWeight,
        )})`
      },
    },
  })
</script>

<style scoped>
  .estimator-answer {
    border: 1px solid rgb(var(--v-theme-border));
    border-radius: 8px;
    padding: 12px 16px;
  }

  .estimator-answer__label {
    font-size: 0.75rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .estimator-answer__value-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /*
    SoCopyToClipboard stacks its slot and its button as blocks, which puts the
    button under the figure. The copy button belongs immediately after the
    number it copies.
  */
  .estimator-answer__value-row :deep(.so-copy) {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .estimator-answer__value {
    font-size: 2rem;
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 0;
    font-variant-numeric: tabular-nums;
  }
</style>
